// Node payloads of the hlod-tileset photogrammetry strategy. Mirrors decode_payload in
// backend/src/Apps/DocumentParser/Python/PhotogrammetryConverter/obj_hlod_tileset_converter.py.

import {
  HS_HLOD_PAYLOAD_VERSION_JPEG,
  HS_HLOD_PAYLOAD_VERSION_KTX2,
} from '../../../shared/hlod/payload-header.function';

/** Payload v1 carries a JPEG atlas, v2 a KTX2 (Basis Universal) atlas; the geometry is the same. */
export { HS_HLOD_PAYLOAD_VERSION_JPEG, HS_HLOD_PAYLOAD_VERSION_KTX2 };
const MAGIC: number[] = [0x48, 0x53, 0x48, 0x4c]; // "HSHL"
const HEADER_SIZE: number = 32;
const FLAG_UINT32_INDICES: number = 1;
const QUANTIZATION_MAX: number = 65535;

export type HsHlodInflate = (data: Uint8Array) => Promise<Uint8Array>;

let crcTable: Uint32Array | null = null;

export function hsCrc32(data: Uint8Array): number {
  if (crcTable === null) {
    crcTable = new Uint32Array(256);
    for (let index: number = 0; index < 256; index++) {
      let value: number = index;
      for (let bit: number = 0; bit < 8; bit++) {
        value = (value & 1) !== 0 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
      }
      crcTable[index] = value >>> 0;
    }
  }
  let crc: number = 0xffffffff;
  for (let index: number = 0; index < data.length; index++) {
    crc = crcTable[(crc ^ data[index]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

export async function hsInflateZlib(data: Uint8Array): Promise<Uint8Array> {
  const stream: ReadableStream<Uint8Array> = new Blob([data]).stream().pipeThrough(new DecompressionStream('deflate'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

export interface HsHlodNodeGeometry {
  /** Positions relative to `origin`, which the caller passes so Float32 keeps full precision. */
  positions: Float32Array;
  normals: Float32Array;
  /** Image convention: v grows downwards, as WebGL samples an unflipped ImageBitmap. */
  uvs: Float32Array;
  indices: Uint32Array;
  /** The encoded atlas: JPEG for the browser's image decoder, or KTX2 for the Basis transcoder. */
  texture: Uint8Array;
  textureCodec: 'jpeg' | 'ktx2';
}

export async function hsDecodeHlodPayload(
  payload: ArrayBuffer,
  origin: readonly [number, number, number],
  inflate: HsHlodInflate = hsInflateZlib
): Promise<HsHlodNodeGeometry> {
  if (payload.byteLength < HEADER_SIZE) {
    throw new Error('HLOD node payload is shorter than its header.');
  }
  const bytes: Uint8Array = new Uint8Array(payload);
  const view: DataView = new DataView(payload);
  if (MAGIC.some((value: number, index: number) => bytes[index] !== value)) {
    throw new Error('HLOD node payload has an unknown signature.');
  }
  const version: number = view.getUint16(4, true);
  if (version !== HS_HLOD_PAYLOAD_VERSION_JPEG && version !== HS_HLOD_PAYLOAD_VERSION_KTX2) {
    throw new Error(`Unsupported HLOD node payload version ${version}.`);
  }
  const flags: number = view.getUint16(6, true);
  const vertexCount: number = view.getUint32(8, true);
  const indexCount: number = view.getUint32(12, true);
  const geometryBytes: number = view.getUint32(16, true);
  const textureBytes: number = view.getUint32(20, true);
  const checksum: number = view.getUint32(24, true);
  const body: Uint8Array = bytes.subarray(HEADER_SIZE);
  if (body.length !== geometryBytes + textureBytes || hsCrc32(body) !== checksum) {
    throw new Error('HLOD node payload checksum mismatch.');
  }
  const geometry: Uint8Array = await inflate(body.subarray(0, geometryBytes));
  const width: number = (flags & FLAG_UINT32_INDICES) !== 0 ? 4 : 2;
  if (geometry.length !== 48 + vertexCount * 10 + indexCount * width) {
    throw new Error('HLOD node payload has an unexpected geometry length.');
  }
  const header: DataView = new DataView(geometry.buffer, geometry.byteOffset, 48);
  const bounds: number[] = Array.from({ length: 6 }, (_: unknown, index: number) => header.getFloat64(index * 8, true));

  const positions: Float32Array = new Float32Array(vertexCount * 3);
  const uvs: Float32Array = new Float32Array(vertexCount * 2);
  let offset: number = 48;
  for (let channel: number = 0; channel < 5; channel++) {
    const low: number = offset;
    const high: number = offset + vertexCount;
    let value: number = 0;
    for (let vertex: number = 0; vertex < vertexCount; vertex++) {
      value = (value + (geometry[low + vertex] | (geometry[high + vertex] << 8))) & 0xffff;
      if (channel < 3) {
        positions[vertex * 3 + channel] =
          bounds[channel] - origin[channel] + (value * bounds[channel + 3]) / QUANTIZATION_MAX;
      } else {
        uvs[vertex * 2 + channel - 3] = value / QUANTIZATION_MAX;
      }
    }
    offset += vertexCount * 2;
  }

  const indices: Uint32Array = new Uint32Array(indexCount);
  let highWater: number = 0;
  for (let index: number = 0; index < indexCount; index++) {
    let code: number = 0;
    for (let plane: number = 0; plane < width; plane++) {
      code += geometry[offset + plane * indexCount + index] * 2 ** (8 * plane);
    }
    const value: number = highWater - code;
    if (value < 0 || value > highWater || value >= vertexCount) {
      throw new Error('HLOD node payload has an invalid index.');
    }
    indices[index] = value;
    if (value === highWater) {
      highWater++;
    }
  }

  return {
    positions,
    normals: hsComputeVertexNormals(positions, indices),
    uvs,
    indices,
    texture: body.subarray(geometryBytes),
    textureCodec: version === HS_HLOD_PAYLOAD_VERSION_KTX2 ? 'ktx2' : 'jpeg',
  };
}

/** Area-weighted vertex normals; the payload omits normals because they are cheap to rebuild. */
export function hsComputeVertexNormals(positions: Float32Array, indices: Uint32Array): Float32Array {
  const normals: Float32Array = new Float32Array(positions.length);
  for (let face: number = 0; face < indices.length; face += 3) {
    const a: number = indices[face] * 3;
    const b: number = indices[face + 1] * 3;
    const c: number = indices[face + 2] * 3;
    const ux: number = positions[b] - positions[a];
    const uy: number = positions[b + 1] - positions[a + 1];
    const uz: number = positions[b + 2] - positions[a + 2];
    const vx: number = positions[c] - positions[a];
    const vy: number = positions[c + 1] - positions[a + 1];
    const vz: number = positions[c + 2] - positions[a + 2];
    const nx: number = uy * vz - uz * vy;
    const ny: number = uz * vx - ux * vz;
    const nz: number = ux * vy - uy * vx;
    for (const vertex of [a, b, c]) {
      normals[vertex] += nx;
      normals[vertex + 1] += ny;
      normals[vertex + 2] += nz;
    }
  }
  for (let vertex: number = 0; vertex < normals.length; vertex += 3) {
    const length: number = Math.hypot(normals[vertex], normals[vertex + 1], normals[vertex + 2]);
    if (length > 0) {
      normals[vertex] /= length;
      normals[vertex + 1] /= length;
      normals[vertex + 2] /= length;
    } else {
      normals[vertex + 2] = 1;
    }
  }
  return normals;
}
